import React, { useCallback, useEffect, useState } from 'react';
import { FieldType } from './field.types';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  RadioGroup,
  SelectChangeEvent,
} from '@mui/material';
import { useFormValidation } from '../context/FormValidation';
import { useSingleForm } from '../context/SingleForm';

type CheckboxFieldProps = FieldType & {
  values?: string[];
};

export const CheckboxField: React.FC<CheckboxFieldProps> = ({
  required,
  fieldKey,
  name,
  defaultValue,
  description,
  values,
  onChangeFieldValue,
}) => {
  const getDefaultValue = () => {
    if (defaultValue && Array.isArray(defaultValue)) {
      return defaultValue;
    } else if (defaultValue) {
      return [defaultValue];
    }
    return [];
  };
  const [checkedValues, setCheckedValues] = useState<string[]>(getDefaultValue());
  const [isValid, setIsValid] = useState<boolean>(true);
  const { showError, updateFieldValidation } = useFormValidation();
  const { formIndex } = useSingleForm();

  const setFieldValidity = useCallback(
    (newIsValid: boolean) => {
      setIsValid(newIsValid);
      updateFieldValidation(`${formIndex}-${fieldKey}`, newIsValid);
    },
    [updateFieldValidation, formIndex, fieldKey]
  );

  useEffect(() => {
    const isInitValid = required ? !!getDefaultValue().length : true;
    setFieldValidity(isInitValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isError = showError && !isValid;

  const onChange = useCallback(
    (event: SelectChangeEvent<any>) => {
      let newValues: string[];
      if (checkedValues.includes(event.target.value)) {
        newValues = checkedValues.filter(value => value !== event.target.value);
      } else {
        newValues = checkedValues.concat([event.target.value]);
      }
      setCheckedValues(newValues);
      setFieldValidity(newValues.length > 0);
      onChangeFieldValue(fieldKey, newValues);
    },
    [onChangeFieldValue, fieldKey, checkedValues, setCheckedValues, setFieldValidity]
  );

  return (
    <FormControl id={`${formIndex}-${fieldKey}`} error={isError}>
      <FormLabel id={fieldKey} className={required ? ' label-required' : ''}>
        {name}
      </FormLabel>
      <RadioGroup name={fieldKey} aria-labelledby={fieldKey}>
        {values?.map(value => {
          return (
            <FormControlLabel
              key={value}
              value={value}
              control={<Checkbox required={required} onChange={onChange} checked={checkedValues.includes(value)} />}
              label={value}
            />
          );
        })}
      </RadioGroup>
      <FormHelperText>{description}</FormHelperText>
    </FormControl>
  );
};
